import { uiPaper } from "shared/ui/uiSurface"
import { DoneAll, NotificationsNone } from "@mui/icons-material"
import { Box, Button, Popover, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import {
  NotificationItem,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
} from "../apis/appApi"

/** Format epoch millis as relative time (e.g. "5 min ago") */
const formatRelativeTime = (
  epochMs: number,
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  const diff = Date.now() - epochMs
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 1) return t("relativeTime.justNow")
  if (minutes < 60) return t("relativeTime.minAgo", { count: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t("relativeTime.hoursAgo", { count: hours })
  return t("relativeTime.daysAgo", { count: Math.floor(hours / 24) })
}

/** App-bar notifications popover (UI-DLV2-009); item → `/orders/:id`. */
export const MainAppbarNotifications: FC<{
  anchorEl: HTMLElement | null
  onClose: () => void
  notifications: NotificationItem[]
}> = ({ anchorEl, onClose, notifications }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [markRead] = useMarkNotificationReadMutation()
  const [markAllRead] = useMarkAllNotificationsReadMutation()
  const unreadCount = notifications.length

  const handleNotificationClick = (notif: {
    id: number
    orderId: number | null
  }) => {
    markRead(notif.id)
    if (notif.orderId) {
      onClose()
      navigate(`/orders/${notif.orderId}`)
    }
  }

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          ...uiPaper("UI-DLV2-009"),
          sx: { width: 360, mt: 1, borderRadius: 2 },
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {t("notifications.title")}
        </Typography>
        {unreadCount > 0 && (
          <Button
            size="small"
            startIcon={<DoneAll sx={{ fontSize: 16 }} />}
            onClick={() => markAllRead()}
            sx={{ textTransform: "none", fontSize: 12 }}
          >
            {t("notifications.markAllRead")}
          </Button>
        )}
      </Box>
      {notifications.length === 0 ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 4, px: 2 }}>
          <NotificationsNone sx={{ fontSize: 32, color: "grey.400" }} />
          <Typography variant="body2" color="text.secondary">
            {t("notifications.noNew")}
          </Typography>
        </Stack>
      ) : (
        <Box sx={{ maxHeight: 400, overflow: "auto" }}>
          {notifications.map((notif) => (
            <Box
              key={notif.id}
              onClick={() => handleNotificationClick(notif)}
              sx={{
                px: 2,
                py: 1.5,
                cursor: "pointer",
                borderBottom: 1,
                borderColor: "divider",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Typography variant="body2" fontWeight={600} noWrap>
                {notif.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {notif.body}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {formatRelativeTime(notif.emissionDate, t)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Popover>
  )
}
